import { configureStore, combineReducers } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";

// ✅ Import your reducers
import authReducer from "./slices/AuthSlice";
import forgotPasswordReducer from "./slices/ForgotPasswordSlice";
import loginMetaReducer from "./slices/LoginBetaSlice";
import passwordSetupReducer from "./slices/ResetPassword";
import usersListReducer from "./slices/UsersListSlice";
import departmentsListReducer from "./slices/DepartmentsListSlice";
import userReducer from "./slices/UserSlice";
import opportunitiesReducer from "./slices/OpportunitiesSlice";
import myTasksReducer from "./slices/MyTasksSlice";
import globalReducer from "./slices/GlobalSlice";
import googleAdsReducer from "./slices/GoogleAdsSlice";
import notificationsReducer from "./slices/NotificationsSlice";
import { baseApi } from "./api/baseApi";
import "./api/opportunitiesApi";

// ✅ Combine reducers
const rootReducer = combineReducers({
  auth: authReducer,
  loginMeta: loginMetaReducer,
  forgotPassword: forgotPasswordReducer,
  passwordSetup: passwordSetupReducer,
  usersList: usersListReducer,
  departmentsList: departmentsListReducer,
  userProfile: userReducer,
  opportunities: opportunitiesReducer,
  myTasks: myTasksReducer,
  global: globalReducer,
  googleAds: googleAdsReducer,
  notifications: notificationsReducer,
  [baseApi.reducerPath]: baseApi.reducer,
});

// ✅ Persist config
const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth"], // 👈 Only persist auth (not loginMeta, since it's temporary OTP info)
};

// ✅ Wrap with persistReducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// ✅ Create store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(baseApi.middleware),
});

// ✅ Persistor for <PersistGate>
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
